<template>
  <div>
    <ClinicPageHeader title="Configurar horários" :subtitle="headerSubtitle">
      <template #actions>
        <router-link
          :to="{ name: 'ClinicDoctorProfile', params: { id: doctorId } }"
          class="btn btn--ghost btn--sm"
        >
          Cancelar
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody" style="max-width:1200px;margin:0 auto;width:100%">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os horários"
        message="Ocorreu um erro ao buscar a configuração de horários. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-6)">
          <div class="skel-line" style="width:50%;height:28px" />
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:280px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- Cabeçalho do médico -->
        <div class="card docadmin-head" style="margin-bottom:var(--space-6)">
          <div class="avatar avatar--xl" :class="doctor.avatarClass">
            {{ doctor.initials }}
          </div>
          <div class="pid" style="flex:1;min-width:0">
            <h2>{{ doctor.name }}</h2>
            <span class="role">
              {{ doctor.specialty }} · {{ doctor.crm }} · {{ doctor.uf }}
            </span>
            <div
              class="row"
              style="gap:var(--space-2);margin-top:var(--space-2)"
            >
              <span class="badge badge--success">{{ doctor.statusLabel }}</span>
              <span class="badge badge--info badge--no-dot">
                {{ slotDuration }} min/consulta
              </span>
              <span class="badge badge--neutral badge--no-dot">
                Presencial e teleconsulta
              </span>
            </div>
          </div>
          <div class="row" style="gap:var(--space-2)">
            <router-link
              :to="{
                name: 'ClinicDoctorSchedule',
                params: { id: String(doctor.id) }
              }"
              class="btn btn--outline btn--sm"
            >
              Ver agenda
            </router-link>
            <router-link
              :to="{
                name: 'ClinicDoctorProfile',
                params: { id: String(doctor.id) }
              }"
              class="btn btn--ghost btn--sm"
            >
              Perfil
            </router-link>
          </div>
        </div>

        <!-- Layout duas colunas -->
        <div class="hours-grid">
          <!-- Coluna principal -->
          <div class="stack" style="gap:var(--space-5)">
            <!-- Horário semanal padrão -->
            <div class="card">
              <fieldset class="fieldset">
                <span class="fieldset-title">Horário semanal padrão</span>
                <p class="field-hint">
                  Define os dias e horários de atendimento recorrentes.
                  Consultas avulsas podem ser ajustadas diretamente na agenda.
                </p>
                <div>
                  <div
                    v-for="day in days"
                    :key="day.id"
                    class="sched-row"
                    :class="{ 'is-off': !day.on }"
                  >
                    <ClinicToggle v-model="day.on" />
                    <span class="sched-badge">{{ day.lbl }}</span>
                    <span class="sched-name">{{ day.name }}</span>
                    <div class="sched-times">
                      <select v-model="day.start" class="select">
                        <option v-for="t in timeOptions" :key="t" :value="t">
                          {{ t }}
                        </option>
                      </select>
                      <span class="sched-sep">→</span>
                      <select v-model="day.end" class="select">
                        <option v-for="t in timeOptions" :key="t" :value="t">
                          {{ t }}
                        </option>
                      </select>
                      <div
                        class="sched-break"
                        :class="{ 'is-hidden': !day.lunch }"
                      >
                        <ClinicToggle v-model="day.lunch" />
                        <span class="sched-break-lbl">Almoço</span>
                        <select v-model="day.ls" class="select">
                          <option v-for="t in timeOptions" :key="t" :value="t">
                            {{ t }}
                          </option>
                        </select>
                        <span class="sched-sep">–</span>
                        <select v-model="day.le" class="select">
                          <option v-for="t in timeOptions" :key="t" :value="t">
                            {{ t }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <span class="sched-slots">{{ slotsLabel(day) }}</span>
                  </div>
                </div>
              </fieldset>
            </div>

            <!-- Parâmetros de consulta -->
            <div class="card">
              <fieldset class="fieldset">
                <span class="fieldset-title">Parâmetros de consulta</span>
                <div class="form-grid" style="margin-top:var(--space-2)">
                  <div class="field">
                    <label class="label">Duração padrão da consulta</label>
                    <select v-model.number="slotDuration" class="select">
                      <option :value="20">20 minutos</option>
                      <option :value="30">30 minutos</option>
                      <option :value="40">40 minutos</option>
                      <option :value="45">45 minutos</option>
                      <option :value="60">60 minutos</option>
                    </select>
                  </div>
                  <div class="field">
                    <label class="label">Intervalo entre consultas</label>
                    <select v-model.number="slotGap" class="select">
                      <option :value="0">Sem intervalo</option>
                      <option :value="10">10 minutos</option>
                      <option :value="15">15 minutos</option>
                      <option :value="20">20 minutos</option>
                    </select>
                  </div>
                  <div class="field">
                    <label class="label">
                      Antecedência mínima para agendar
                    </label>
                    <select v-model="minLead" class="select">
                      <option>Sem restrição</option>
                      <option>1 hora antes</option>
                      <option>24 horas antes</option>
                      <option>48 horas antes</option>
                      <option>72 horas antes</option>
                    </select>
                  </div>
                  <div class="field">
                    <label class="label">
                      Agendamento máximo com antecedência
                    </label>
                    <select v-model="maxLead" class="select">
                      <option>30 dias</option>
                      <option>60 dias</option>
                      <option>90 dias</option>
                      <option>6 meses</option>
                    </select>
                  </div>
                </div>
              </fieldset>
            </div>

            <!-- Vigência -->
            <div class="card">
              <fieldset class="fieldset">
                <span class="fieldset-title">Vigência deste horário</span>
                <p class="field-hint">
                  Se houver um horário anterior, ele será desativado na data de
                  início abaixo.
                </p>
                <div class="form-grid">
                  <div class="field">
                    <label class="label">Válido a partir de</label>
                    <input v-model="validFrom" class="input" type="date" />
                  </div>
                  <div class="field">
                    <label class="label">
                      Válido até
                      <span
                        style="color:var(--color-gray-400);font-weight:var(--fw-regular)"
                      >
                        (opcional)
                      </span>
                    </label>
                    <div class="date-ph-wrap">
                      <input
                        v-model="validTo"
                        class="input"
                        :class="{ 'input--date-empty': !validTo }"
                        type="date"
                      />
                      <span v-if="!validTo" class="date-ph">00/00/0000</span>
                    </div>
                    <span
                      class="field-hint"
                      style="margin:0;font-size:var(--font-size-xs)"
                    >
                      Deixe em branco para o horário valer sem data de fim.
                    </span>
                  </div>
                </div>
              </fieldset>
            </div>

            <!-- Bloqueios pontuais -->
            <div class="card">
              <fieldset class="fieldset">
                <span class="fieldset-title">Bloqueios pontuais</span>
                <p class="field-hint">
                  Dias ou períodos em que a médica não atende — férias,
                  congresso, licença, etc. Consultas já agendadas não são
                  canceladas automaticamente.
                </p>
                <div v-if="exceptions.length">
                  <div v-for="(exc, i) in exceptions" :key="i" class="exc-row">
                    <input v-model="exc.start" class="input" type="date" />
                    <span class="sched-sep">até</span>
                    <input v-model="exc.end" class="input" type="date" />
                    <div class="exc-label">
                      <input
                        v-model="exc.reason"
                        class="input"
                        placeholder="Motivo (opcional)"
                      />
                    </div>
                    <button
                      class="exc-del"
                      title="Remover"
                      @click="removeException(i)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <button
                  class="btn btn--ghost btn--sm"
                  style="margin-top:var(--space-3);align-self:flex-start"
                  @click="addException"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  Adicionar bloqueio
                </button>
              </fieldset>
            </div>

            <!-- Ações -->
            <div
              class="row"
              style="justify-content:flex-end;gap:var(--space-3);padding-bottom:var(--space-8)"
            >
              <router-link
                :to="{
                  name: 'ClinicDoctorProfile',
                  params: { id: String(doctor.id) }
                }"
                class="btn btn--ghost"
              >
                Cancelar
              </router-link>
              <!-- Proto usa btn--accent; regra do módulo troca CTA para btn--primary -->
              <button
                v-if="canEdit('medicos')"
                class="btn btn--primary"
                :disabled="saving"
                @click="save"
              >
                {{ saving ? "Salvando…" : "Salvar horários" }}
              </button>
            </div>
            <p
              v-if="saveError"
              class="field-error"
              data-test="save-error"
              role="alert"
              style="text-align:right;margin-top:var(--space-2)"
            >
              {{ saveError }}
            </p>
          </div>

          <!-- Aside: resumo -->
          <div class="hours-aside">
            <div class="card">
              <span class="fieldset-title">Resumo semanal</span>
              <p
                style="font-size:var(--font-size-xs);color:var(--color-gray-500);margin:4px 0 var(--space-4)"
              >
                Baseado no horário configurado à esquerda.
              </p>
              <div style="margin-bottom:var(--space-4)">
                <div v-for="day in days" :key="day.id" class="prev-bar-row">
                  <span class="prev-lbl">{{ day.lbl }}</span>
                  <div class="prev-track">
                    <div
                      class="prev-fill"
                      :class="{ off: !day.on }"
                      :style="{ width: barPct(day) + '%' }"
                    />
                  </div>
                  <span class="prev-ct">
                    {{ day.on ? computeVagas(day) + " vg" : "—" }}
                  </span>
                </div>
              </div>
              <hr
                style="border:none;border-top:1px solid var(--color-gray-100);margin:var(--space-4) 0"
              />
              <div class="aside-kpi">
                <div class="aside-kpi-item">
                  <div class="v">{{ activeDays }}</div>
                  <div class="l">dias/semana</div>
                </div>
                <div class="aside-kpi-item">
                  <div class="v">{{ totalSlots }}</div>
                  <div class="l">vagas/semana</div>
                </div>
                <div class="aside-kpi-item">
                  <div class="v">{{ totalHours }}h</div>
                  <div class="l">horas atend.</div>
                </div>
                <div class="aside-kpi-item">
                  <div class="v">{{ monthlyEstimate }}</div>
                  <div class="l">vagas/mês est.</div>
                </div>
              </div>
            </div>

            <div
              v-if="currentSchedule"
              class="card"
              style="margin-top:var(--space-4)"
            >
              <span class="fieldset-title">Horário atual vigente</span>
              <div class="dl" style="margin-top:var(--space-3)">
                <div v-if="currentSchedule.from">
                  <dt>Início</dt>
                  <dd>{{ currentSchedule.from }}</dd>
                </div>
                <div>
                  <dt>Dias</dt>
                  <dd>{{ currentSchedule.days }}</dd>
                </div>
                <div v-if="currentSchedule.range">
                  <dt>Horário</dt>
                  <dd>{{ currentSchedule.range }}</dd>
                </div>
                <div>
                  <dt>Duração</dt>
                  <dd>
                    {{ currentSchedule.duration }} min +
                    {{ currentSchedule.gap }} min gap
                  </dd>
                </div>
                <div>
                  <dt>Vagas/sem.</dt>
                  <dd>{{ currentSchedule.slots }}</dd>
                </div>
              </div>
              <p
                style="font-size:var(--font-size-xs);color:var(--color-gray-400);margin-top:var(--space-3)"
              >
                Será substituído ao salvar o novo horário.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicToggle from "@/components/clinica/ClinicToggle.vue";
import { ClinicDoctors, ClinicConfig } from "@/services/clinica";

// Data local de hoje (AAAA-MM-DD): a grade só gera horários a partir de hoje.
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

// Mensagem da API: { error } ou a 1ª de record_invalid (ex.: conflito de horário).
function apiErrorMessage(error) {
  const body = (error && error.response && error.response.data) || {};
  if (typeof body.error === "string") return body.error;
  const errors = body.errors || {};
  const first = Object.values(errors).find(v => Array.isArray(v) && v.length);
  return first ? first[0] : "";
}

function toMin(t) {
  const [h, m] = t.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

// key do dia → rótulo curto (badge) e nome completo (coluna).
const DAY_META = {
  seg: { lbl: "SEG", name: "Segunda-feira" },
  ter: { lbl: "TER", name: "Terça-feira" },
  qua: { lbl: "QUA", name: "Quarta-feira" },
  qui: { lbl: "QUI", name: "Quinta-feira" },
  sex: { lbl: "SEX", name: "Sexta-feira" },
  sab: { lbl: "SÁB", name: "Sábado" },
  dom: { lbl: "DOM", name: "Domingo" }
};

// Horário semanal padrão para médicos ainda sem grade configurada.
function defaultDays() {
  return [
    {
      id: "seg",
      lbl: "SEG",
      name: "Segunda-feira",
      on: true,
      start: "08:00",
      end: "17:00",
      lunch: true,
      ls: "12:00",
      le: "13:00"
    },
    {
      id: "ter",
      lbl: "TER",
      name: "Terça-feira",
      on: true,
      start: "08:00",
      end: "17:00",
      lunch: true,
      ls: "12:00",
      le: "13:00"
    },
    {
      id: "qua",
      lbl: "QUA",
      name: "Quarta-feira",
      on: true,
      start: "08:00",
      end: "17:00",
      lunch: true,
      ls: "12:00",
      le: "13:00"
    },
    {
      id: "qui",
      lbl: "QUI",
      name: "Quinta-feira",
      on: false,
      start: "08:00",
      end: "17:00",
      lunch: true,
      ls: "12:00",
      le: "13:00"
    },
    {
      id: "sex",
      lbl: "SEX",
      name: "Sexta-feira",
      on: true,
      start: "08:00",
      end: "17:00",
      lunch: true,
      ls: "12:00",
      le: "13:00"
    },
    {
      id: "sab",
      lbl: "SÁB",
      name: "Sábado",
      on: false,
      start: "08:00",
      end: "12:00",
      lunch: false,
      ls: "10:00",
      le: "10:30"
    },
    {
      id: "dom",
      lbl: "DOM",
      name: "Domingo",
      on: false,
      start: "09:00",
      end: "12:00",
      lunch: false,
      ls: "10:00",
      le: "10:30"
    }
  ];
}

export default {
  name: "ClinicDoctorHours",
  components: { ClinicPageHeader, ClinicStateBox, ClinicToggle },
  mixins: [clinicCan],
  data() {
    return {
      // loading → ready/error. Preview de estados via ?state= na URL.
      state: "loading",
      doctor: null, // carregado da API no fetch() (antes vinha do stub)
      slotDuration: 30,
      slotGap: 10,
      minLead: "24 horas antes",
      maxLead: "60 dias",
      validFrom: "2026-07-01",
      validTo: "",
      saving: false,
      saveError: "",
      // Snapshot do horário atualmente vigente (preenchido no fetch real).
      currentSchedule: null,
      // Horário semanal padrão — carregado da API no created().
      days: defaultDays(),
      exceptions: []
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      // Médico real (antes vinha do stub). Independente do schedule: uma falha ao
      // carregar a grade não deve esconder a identificação do médico.
      try {
        const { data } = await ClinicDoctors.get(this.doctorId);
        this.doctor = data;
      } catch (e) {
        this.doctor = null;
      }
      try {
        // schedule() é somente-leitura; preview de erro via ?state=error.
        if (this.$route.query.state === "error") throw new Error("preview");
        const { data } = await ClinicDoctors.schedule(this.doctorId);
        this.applySchedule(data);
        if (!data) await this.applyDefaultDuration();
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    applySchedule(data) {
      // Sem grade cadastrada → começa de um padrão editável.
      if (!data) {
        this.slotDuration = 30;
        this.slotGap = 10;
        this.validFrom = todayIso();
        this.validTo = "";
        this.days = defaultDays();
        this.exceptions = [];
        return;
      }
      this.slotDuration = data.duration;
      this.slotGap = data.gap;
      this.validFrom = data.validity ? data.validity.from : "";
      this.validTo = data.validity ? data.validity.to : "";
      this.days = (data.days || []).map(d => ({
        id: d.key,
        lbl: (DAY_META[d.key] || {}).lbl || (d.label || "").toUpperCase(),
        name: (DAY_META[d.key] || {}).name || d.label,
        on: d.active,
        start: d.start,
        end: d.end,
        lunch: d.lunch,
        ls: d.lunchStart,
        le: d.lunchEnd
      }));
      this.exceptions = (data.blocks || []).map(b => ({
        start: b.from,
        end: b.to,
        reason: b.label
      }));
      this.currentSchedule = this.snapshotSchedule();
    },
    // Snapshot congelado do horário carregado (o "vigente"), derivado dos
    // dados reais — o card lateral não usa mais valores hardcoded.
    snapshotSchedule() {
      const active = this.days.filter(d => d.on);
      if (!active.length) return null;
      const starts = active.map(d => d.start).filter(Boolean);
      const ends = active.map(d => d.end).filter(Boolean);
      const range =
        starts.length && ends.length
          ? `${starts.reduce((a, b) =>
              a < b ? a : b
            )} – ${ends.reduce((a, b) => (a > b ? a : b))}`
          : "";
      return {
        from: this.validFrom,
        days: active.map(d => d.name).join(" · "),
        range,
        duration: this.slotDuration,
        gap: this.slotGap,
        slots: this.totalSlots
      };
    },
    buildPayload() {
      return {
        duration: this.slotDuration,
        gap: this.slotGap,
        validity: { from: this.validFrom, to: this.validTo },
        days: this.days.map(d => ({
          key: d.id,
          label: (DAY_META[d.id] || {}).name || d.name,
          active: d.on,
          start: d.start,
          end: d.end,
          lunch: d.lunch,
          lunchStart: d.ls,
          lunchEnd: d.le
        })),
        blocks: this.exceptions.map((e, i) => ({
          id: i + 1,
          label: e.reason,
          from: e.start,
          to: e.end
        }))
      };
    },
    workMinutes(day) {
      if (!day.on) return 0;
      const lunch = day.lunch ? Math.max(0, toMin(day.le) - toMin(day.ls)) : 0;
      return Math.max(0, toMin(day.end) - toMin(day.start) - lunch);
    },
    computeVagas(day) {
      const work = this.workMinutes(day);
      if (work <= 0) return 0;
      return Math.floor(work / (this.slotDuration + this.slotGap));
    },
    slotsLabel(day) {
      if (!day.on) return "Inativo";
      const n = this.computeVagas(day);
      return `${n} vaga${n !== 1 ? "s" : ""}`;
    },
    barPct(day) {
      if (!day.on) return 100;
      return Math.min(100, Math.round((this.workMinutes(day) / 480) * 100));
    },
    addException() {
      this.exceptions.push({ start: "", end: "", reason: "" });
    },
    removeException(i) {
      this.exceptions.splice(i, 1);
    },
    // Grade nova: duração padrão da especialidade do médico na clínica (Config).
    async applyDefaultDuration() {
      if (!this.doctor || !this.doctor.specialty) return;
      try {
        const { data } = await ClinicConfig.clinicSpecialties();
        const match = (data || []).find(
          sp => sp.name === this.doctor.specialty && sp.defaultDuration
        );
        if (match) this.slotDuration = match.defaultDuration;
      } catch (e) {
        // sem a lista, fica o padrão de 30 min
      }
    },
    async save() {
      this.saving = true;
      this.saveError = "";
      try {
        await ClinicDoctors.saveSchedule(this.doctorId, this.buildPayload());
        this.$toast.success("Horários salvos");
      } catch (e) {
        this.saveError = apiErrorMessage(e);
        this.$toast.error(
          this.saveError || "Não foi possível salvar os horários"
        );
      } finally {
        this.saving = false;
      }
    }
  },
  computed: {
    doctorId() {
      return this.$route.params.id;
    },
    // Subtítulo do header (sempre renderizado) — guarda doctor null durante o load.
    headerSubtitle() {
      const d = this.doctor;
      return d ? `${d.name} · ${d.specialty} · ${d.crm}` : "Carregando…";
    },
    // Opções de horário em passos de 30 min (06:00 → 21:00).
    timeOptions() {
      const opts = [];
      for (let h = 6; h <= 21; h++) {
        ["00", "30"].forEach(m => {
          if (h === 21 && m === "30") return;
          opts.push(`${String(h).padStart(2, "0")}:${m}`);
        });
      }
      return opts;
    },
    activeDays() {
      return this.days.filter(d => d.on).length;
    },
    totalSlots() {
      return this.days.reduce((sum, d) => sum + this.computeVagas(d), 0);
    },
    totalHours() {
      const mins = this.days.reduce((sum, d) => sum + this.workMinutes(d), 0);
      return Math.round(mins / 60);
    },
    monthlyEstimate() {
      return Math.round(this.totalSlots * 4.33);
    }
  }
};
</script>

<style lang="scss" scoped>
.hours-grid {
  display: grid;
  grid-template-columns: 1fr 272px;
  gap: var(--space-6);
  align-items: start;
}
.hours-aside {
  position: sticky;
  top: var(--space-4);
}
@media (max-width: 900px) {
  .hours-grid {
    grid-template-columns: 1fr;
  }
  .hours-aside {
    position: static;
  }
}
@media (max-width: 600px) {
  .sched-times {
    padding-left: 0;
  }
}

.field-hint {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: -2px 0 var(--space-4);
}

// Date nativo vazio mostra "mm/dd/yyyy" (idioma do Chrome) e não aceita
// placeholder: esconde esse texto e sobrepõe "00/00/0000" (sem capturar clique,
// então o calendário continua funcionando). Some ao focar.
.date-ph-wrap {
  position: relative;
}
.input--date-empty:not(:focus)::-webkit-datetime-edit {
  color: transparent;
}
.date-ph {
  position: absolute;
  left: var(--space-4);
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  pointer-events: none;
}
.input:focus + .date-ph {
  display: none;
}

/* ── Linha de dia ── */
.sched-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-gray-100);
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }
  &.is-off .sched-badge {
    background: var(--color-gray-100);
    color: var(--color-gray-400);
  }
  &.is-off .sched-name {
    color: var(--color-gray-400);
  }
  &.is-off .sched-times {
    opacity: 0.3;
    pointer-events: none;
  }
  &.is-off .sched-slots {
    color: var(--color-gray-400);
    background: var(--color-gray-100);
  }
}
.sched-badge {
  width: 42px;
  height: 42px;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
  color: var(--color-primary-dark);
  font-size: var(--font-size-xs);
  font-weight: var(--fw-extra);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  transition: background 0.15s, color 0.15s;
  letter-spacing: 0.04em;
}
.sched-name {
  width: 124px;
  flex: none;
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-700);
  transition: color 0.15s;
}
.sched-times {
  // 2ª linha do dia, alinhada ao nome; o bloco de almoço desce se não couber.
  order: 2;
  flex-basis: 100%;
  padding-left: calc(44px + 42px + 2 * var(--space-3));
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  transition: opacity 0.15s;

  .select {
    height: 36px;
    width: 88px;
    font-size: var(--font-size-sm);
    padding: 0 var(--space-2);
  }
}
.sched-sep {
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  flex: none;
}
.sched-break {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding-left: var(--space-3);
  border-left: 1px solid var(--color-gray-200);
  margin-left: var(--space-1);

  &.is-hidden {
    opacity: 0.3;
    pointer-events: none;
  }
}
.sched-break-lbl {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
}
.sched-slots {
  order: 1;
  margin-left: auto;
  flex: none;
  font-family: var(--font-num);
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-primary);
  background: var(--color-primary-light);
  padding: 4px 10px;
  border-radius: var(--radius-full);
  white-space: nowrap;
  min-width: 60px;
  text-align: center;
}

/* ── Resumo visual (aside) ── */
.prev-bar-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}
.prev-lbl {
  width: 30px;
  flex: none;
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-gray-500);
}
.prev-track {
  flex: 1;
  height: 12px;
  background: var(--color-gray-100);
  border-radius: var(--radius-full);
  overflow: hidden;
}
.prev-fill {
  height: 100%;
  background: var(--color-primary);
  border-radius: var(--radius-full);
  transition: width 0.35s ease;

  &.off {
    background: var(--color-gray-200);
  }
}
.prev-ct {
  width: 44px;
  flex: none;
  text-align: right;
  font-family: var(--font-num);
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

/* ── Bloqueios pontuais ── */
.exc-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);

  &:last-child {
    border-bottom: none;
  }
  .input,
  .select {
    height: 36px;
    font-size: var(--font-size-sm);
  }
  .input[type="date"] {
    width: 140px;
  }
  .exc-label {
    flex: 1;

    .input {
      width: 100%;
    }
  }
}
.exc-del {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
  background: none;
  color: var(--color-gray-400);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  &:hover {
    border-color: var(--color-danger);
    color: var(--color-danger);
    background: var(--color-danger-light);
  }
}

/* ── Stat mini (aside) ── */
.aside-kpi {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  margin-top: var(--space-4);
}
.aside-kpi-item {
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  padding: var(--space-3);

  .v {
    font-family: var(--font-num);
    font-size: var(--font-size-xl);
    font-weight: var(--fw-extra);
    color: var(--color-gray-900);
    line-height: 1;
  }
  .l {
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
    margin-top: 3px;
  }
}
</style>
